import type { ReactNode } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { parseDecimalInput, type DecimalInput } from '../lib/decimal';
import { cx } from './cx';
import styles from './Field.module.css';
import { TextField, type FieldChromeProps } from './TextField';

export interface DecimalFieldProps extends FieldChromeProps {
  /** Raw text as typed. Parse with parseDecimalInput() to get the API decimal string. */
  value: string;
  onChange: (value: string) => void;
  maxFractionDigits?: number;
  placeholder?: string;
  suffix?: ReactNode;
  isDisabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

/** Message for a malformed decimal (format only; business limits are the server's). */
export function useDecimalProblem(value: string, maxFractionDigits = 5): string | undefined {
  const { t } = useI18n();
  return decimalProblemMessage(t.validation, parseDecimalInput(value, maxFractionDigits));
}

export function decimalProblemMessage(
  messages: { decimalInvalid: string; decimalTooManyDecimals: string; decimalTooLarge: string },
  parsed: DecimalInput,
): string | undefined {
  if (parsed.kind !== 'invalid') return undefined;
  if (parsed.problem === 'too_many_decimals') return messages.decimalTooManyDecimals;
  if (parsed.problem === 'too_large') return messages.decimalTooLarge;
  return messages.decimalInvalid;
}

/**
 * Text input for amounts, quantities and rates. Keeps the value as text (never a JS number);
 * accepts Arabic-Indic digits and normalizes them when the field loses focus.
 */
export function DecimalField({
  value,
  onChange,
  maxFractionDigits = 5,
  errorMessage,
  className,
  ...props
}: DecimalFieldProps) {
  const formatProblem = useDecimalProblem(value, maxFractionDigits);
  return (
    <TextField
      {...props}
      value={value}
      onChange={onChange}
      inputMode="decimal"
      autoComplete="off"
      errorMessage={formatProblem ?? errorMessage}
      className={className}
      inputClassName={cx(styles.numeric)}
      onBlur={() => {
        const parsed = parseDecimalInput(value, maxFractionDigits);
        if (parsed.kind === 'valid' && parsed.value !== value) onChange(parsed.value);
      }}
    />
  );
}
